import Swal from "sweetalert2";
import { observer } from "@/observer";
import { isSessionExpiredError } from "@/publicjs/authSession";

const normalizeAlertMessage = (msg) => {
  if (typeof msg === "string") {
    return msg;
  }

  if (typeof msg?.response?.data?.message === "string") {
    return msg.response.data.message;
  }

  if (typeof msg?.response?.data?.error === "string") {
    return msg.response.data.error;
  }

  if (typeof msg?.response?.data === "string") {
    return msg.response.data;
  }

  if (typeof msg?.message === "string") {
    return msg.message;
  }

  return "Ocurrió un error inesperado.";
};

const blurActiveElement = () => {
  const activeElement = document.activeElement;
  if (activeElement && typeof activeElement.blur === "function") {
    activeElement.blur();
  }
};

const observeSwalContainer = () => {
  observer.observe(
    document.querySelector(".swal2-container"),
    { box: "content-box" },
    10,
  );
};
/**
 *
 * @param {*} register | Nombre del registro que se va a eliminar
 * @param {*} action | accion a ejecutar
 */
const confirmDelete = (register, action) => {
  Swal.fire({
    title: "¿Estas seguro que deseas eliminar el registro?",
    text: `Si eliminas a (${register}), ya no podrás recuperarlo más adelante!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#315df5",
    cancelButtonColor: "#dc2626",
    confirmButtonText: "Si, ¡eliminar!",
    cancelButtonText: "Cancelar",
    width: "300px",
  }).then((result) => {
    if (result.isConfirmed) {
      action();
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: `${register}, a sido eliminado de la base de datos`,
        confirmButtonColor: "#315df5",
        confirmButtonText: "Aceptar!",
      });
    }
  });
};
/**
 *
 * @param {*} msg | Puede ser el nombre del producto o el registro insertado
 */
const confirmationOfRecord = (msg, options = {}) => {
  const {
    title = "Registrado",
    timer,
    toast,
    position,
    showConfirmButton,
    confirmButtonText = "Aceptar!",
  } = options;

  Swal.fire({
    icon: "success",
    title,
    html: `${msg} a sido registrado con éxito a la base de datos.`,
    confirmButtonColor: "#315df5",
    confirmButtonText,
    ...(typeof timer === "number" ? { timer, timerProgressBar: true } : {}),
    ...(typeof toast === "boolean" ? { toast } : {}),
    ...(position ? { position } : {}),
    ...(typeof showConfirmButton === "boolean" ? { showConfirmButton } : {}),
  });
  observeSwalContainer();
};
/**
 *
 * @param {*} msg | Puede ser el nombre del producto o el registro actualizado
 */
const confirmationOfUpdate = (msg) => {
  Swal.fire({
    icon: "success",
    title: "Actualizado",
    html: `<strong>${msg}</strong> a sido actualizado con éxito.`,
    confirmButtonColor: "#315df5",
    confirmButtonText: "Aceptar!",
  });
  observeSwalContainer();
};
/**
 *
 * @param {*} msg | Puede ser el nombre del producto o el registro eliminado
 */
const errorActions = (msg) => {
  if (isSessionExpiredError(msg)) {
    Swal.close();
    return;
  }

  blurActiveElement();
  Swal.fire({
    icon: "error",
    title: "Error",
    html: normalizeAlertMessage(msg),
    confirmButtonColor: "#315df5",
    confirmButtonText: "Aceptar!",
  });
  observeSwalContainer();
};

const recoverableErrorAction = (msg, options = {}) => {
  if (isSessionExpiredError(msg)) {
    return;
  }

  const {
    title = "No se pudo actualizar una sección",
    notify = false,
    icon = "warning",
    toast = true,
    position = "top-end",
    timer = 3500,
    confirmButtonText = "Aceptar!",
  } = options;

  const normalizedMessage = normalizeAlertMessage(msg);

  console.warn(`[recoverable-error] ${title}: ${normalizedMessage}`, msg);

  if (!notify) {
    return;
  }

  blurActiveElement();
  Swal.fire({
    icon,
    title,
    html: normalizedMessage,
    toast,
    position,
    timer,
    timerProgressBar: toast,
    showConfirmButton: !toast,
    confirmButtonColor: "#315df5",
    confirmButtonText,
  });
  observeSwalContainer();
};
/**
 *
 * @param {*} msg | Puede ser el nombre del producto o el registro eliminado
 */
const warningActions = (msg) => {
  blurActiveElement();
  Swal.fire({
    icon: "warning",
    title: "Uppsss...",
    html: msg,
    confirmButtonColor: "#315df5",
    confirmButtonText: "Aceptar!",
  });
  observeSwalContainer();
};

const notificationAction = ({
  title = "Notificacion",
  msg = "",
  icon = "info",
  confirmButtonText = "Aceptar!",
}) => {
  blurActiveElement();
  const alertPromise = Swal.fire({
    icon,
    title,
    html: msg,
    confirmButtonColor: "#315df5",
    confirmButtonText,
  });

  observer.observe(
    document.querySelector(".swal2-container"),
    { box: "content-box" },
    10,
  );

  return alertPromise;
};

const uploadData = async (title, msg) => {
  Swal.fire({
    // icon: "warning",
    title: title,
    html: msg,
    didOpen: () => {
      Swal.showLoading();
      //codigo para evitar el error de [resize observer]
      observer.observe(
        document.querySelector(".swal2-container"),
        { box: "content-box" },
        10,
      );
    },
  });
  //codigo para evitar el error de [resize observer]
  observer.observe(
    document.querySelector(".swal2-container"),
    { box: "content-box" },
    10,
  );
};

const closeAlert = async () => {
  Swal.close();
};

/**
 *
 * @param {*} title | titulo del mensaje de elminación
 * @param {*} msg | mensaje para una accion correcta
 */
const successAction = async (title, msg) => {
  Swal.fire({
    icon: "success",
    title,
    html: msg,
    confirmButtonColor: "#315df5",
    confirmButtonText: "Aceptar!",
  });
  observer.observe(
    document.querySelector(".swal2-container"),
    { box: "content-box" },
    10,
  );
};

export {
  confirmDelete,
  confirmationOfRecord,
  confirmationOfUpdate,
  errorActions,
  recoverableErrorAction,
  warningActions,
  notificationAction,
  uploadData,
  closeAlert,
  successAction,
};
